// ============================================================
// SAUSAGE CALCULATOR — Ingredient & Reference Databases
// Carried over from the Google Sheets version (v2.4c)
// ============================================================

export const SECTIONS = [
  { key: 'meat',    label: 'Lean Meat',           color: 'var(--cat-meat)',      rows: 4,  typMin: 50,  typMax: 80  },
  { key: 'fat',     label: 'Fat',                  color: 'var(--cat-fat)',       rows: 2,  typMin: 20,  typMax: 30  },
  { key: 'liquid',  label: 'Liquids',              color: 'var(--cat-liquid)',    rows: 3,  typMin: 5,   typMax: 20  },
  { key: 'salt',    label: 'Salt',                 color: 'var(--cat-salt)',      rows: 2,  typMin: 1.5, typMax: 2.0 },
  { key: 'cure',    label: 'Curing Agents',        color: 'var(--cat-cure)',      rows: 1,  typMin: 0,   typMax: 0.25, isCure: true },
  { key: 'sweet',   label: 'Sweeteners',           color: 'var(--cat-sweet)',     rows: 3,  typMin: 0.5, typMax: 1.0 },
  { key: 'binder',  label: 'Binders',              color: 'var(--cat-binder)',    rows: 4,  typMin: 2.0, typMax: 4.0 },
  { key: 'cheese',  label: 'High Temp Cheese',     color: 'var(--cat-cheese)',    rows: 3,  typMin: 5.0, typMax: 10.0 },
  { key: 'phos',    label: 'Phosphates',           color: 'var(--cat-phosphate)', rows: 1,  typMin: 0.3, typMax: 0.5 },
  { key: 'accel',   label: 'Curing Accelerators',  color: 'var(--cat-accel)',     rows: 2,  typMin: 0.5, typMax: 0.5 },
  { key: 'flavor',  label: 'Flavor Enhancers',     color: 'var(--cat-flavor)',    rows: 3,  typMin: 0.05, typMax: 1.0 },
  { key: 'spice',   label: 'Spices & Seasonings',  color: 'var(--cat-spice)',     rows: 15, typMin: 0.5, typMax: 2.0 },
  { key: 'casing',  label: 'Casings',                color: 'var(--cat-casing)',    rows: 1,  typMin: 0,   typMax: 0,   isCasingSection: true },
];

// DB[key] = array of [name, typMin%, typMax%, hardMax%, note]
export const DB = {
  meat: [
    ['Pork Shoulder (Boston Butt)', 50, 80, 100, 'Most versatile — natural fat-to-lean ratio (~70/30). Often needs no added fat.'],
    ['Pork Loin',                   50, 80, 100, 'Very lean (~3.5% fat) — combine with back fat to reach 20-30% total fat.'],
    ['Beef Chuck (80/20)',          50, 80, 100, 'Already 20% fat — may need no added fat.'],
    ['Beef Brisket (flat)',         50, 80, 100, 'Lean (~7% fat); bold beef flavor.'],
    ['Beef Tri-tip',                50, 80, 100, 'Lean (~10% fat); good for beef-forward sausages.'],
    ['Sirloin Tip / Eye Round',     50, 80, 100, 'Very lean (~4% fat); trim all silver skin before grinding.'],
    ['Brisket Point',               50, 80, 100, 'Fattier than the flat (~22% fat); may need no added fat.'],
    ['Chicken Thigh (boneless)',    50, 80, 100, '~8% fat — add separate pork fat to reach target fat %.'],
    ['Turkey Thigh (boneless)',     50, 80, 100, '~5% fat — add pork fat to reach target.'],
    ['Venison',                     50, 80, 100, 'Extremely lean (~2.4% fat) — always add 20-30% pork back fat.'],
    ['Wild Boar',                   50, 80, 100, '~4.4% fat — add back fat to reach target.'],
    ['Lamb Shoulder',               50, 80, 100, '~16% fat; pairs well with Mediterranean spices.'],
    ['Custom Meat',                  0, 100, 100, 'Macro estimate uses Beef Chuck values as fallback.'],
  ],
  fat: [
    ['Pork Back Fat',     20, 30, 40, 'Standard sausage fat (~88.7% fat). GOLDEN: 20-30% of total meat weight.'],
    ['Pork Belly (skin-off)', 20, 35, 45, '~53% fat — contributes both lean and fat.'],
    ['Pork Jowl',         20, 30, 40, '~72% fat with some lean; slightly softer than back fat.'],
    ['Beef Fat (Suet)',   20, 30, 40, '~94% fat. Very hard — use for firm texture.'],
    ['Duck Fat',          15, 25, 35, '~100% fat, lower melt point.'],
    ['Lard (rendered)',   15, 25, 35, '~100% fat, very soft.'],
    ['Chicken Skin',      10, 25, 40, 'Raw skin ~32% fat. Adds rich flavor and fat. Grind with meat.'],
    ['Soy Protein Emulsion', 10, 30, 40, '1:4:5 ratio of soy protein isolate/oil/water. Adds protein while contributing fat. Mix fresh before use.'],
    ['Custom Fat',         0, 30, 45, 'Macro estimate uses Pork Back Fat values as fallback.'],
  ],
  liquid: [
    ['Ice (crushed/shaved)',       5, 10, 30, 'PREFERRED. Keeps meat below 34°F during mixing.'],
    ['Cold Water (near 32°F)',     5, 10, 30, 'Use only if ice unavailable.'],
    ['Beer',                       5, 10, 20, 'Adds malt/grain flavor. Use cold.'],
    ['Stock (Chicken)',            5, 10, 20, 'Adds savory depth. Use well-chilled.'],
    ['Stock (Beef)',               5, 10, 20, 'Bold umami base. Use well-chilled.'],
    ['Stock (Pork)',               5, 10, 20, 'Mild, savory. Use well-chilled.'],
    ['Wine (Red)',                 5, 10, 15, 'Pairs well with beef, lamb, venison.'],
    ['Wine (White)',               5, 10, 15, 'Pairs well with pork, chicken, veal.'],
    ['Milk',                       5, 10, 15, 'Adds mild richness; use cold.'],
    ['Apple Juice',                5, 10, 20, 'Mild sweetness and fruit notes. Use cold.'],
    ['Heavy Cream',                5, 10, 15, 'Adds richness and fat. Use cold.'],
    ['Soy Sauce',                  2,  4,  4, 'Adds umami and salt. Reduce salt when using.'],
  ],
  salt: [
    ['Non-iodized Table Salt',        1.5, 2.0, 2.5, 'GOLDEN: 1.75-2.0%. NEVER use iodized salt.'],
    ['Kosher Salt (Diamond Crystal)', 1.5, 2.0, 2.5, 'Always measure by weight (grams), not volume.'],
    ['Kosher Salt (Morton)',          1.5, 2.0, 2.5, 'Denser than Diamond Crystal. Always weigh it.'],
    ['Fine Sea Salt',                 1.5, 2.0, 2.5, 'Use fine-ground only for even distribution.'],
  ],
  cure: [
    ['Prague Powder #1 (Cure #1)', 0.20, 0.25, 0.25,
      'HARD MAX: 0.25% = 156 ppm (USDA limit). Required: hot-smoked, cold-smoked, overnight rest.'],
    ['Cultured Celery Juice Powder', 0.35, 0.42, 0.50,
      'Natural cure — delivers ~125ppm nitrite at 0.42%. Pair with Acerola Cherry Powder as curing accelerator. USDA label: "uncured / no nitrites added." Source: The Sausage Maker.'],
    ['EcoCure #1', 0.80, 1.00, 1.00,
      'Polyphenol-based natural curing agent (The Sausage Maker). Use 1% of total meat+fat weight. Add AFTER all other ingredients have been mixed. No starter culture required.'],
  ],
  sweet: [
    ['Dextrose (glucose)',        0.5, 1.0, 2.0, 'PREFERRED for cured products. Mild sweetness.'],
    ['White Granulated Sugar',   0.5, 1.0, 2.0, ''],
    ['Brown Sugar',              0.5, 1.0, 2.0, 'Molasses/caramel notes.'],
    ['Corn Syrup Solids',        0.5, 1.0, 2.0, ''],
    ['Honey Powder',             0.5, 1.0, 2.0, ''],
    ['Maple Sugar (granulated)', 0.5, 1.0, 2.0, ''],
    ['Unsweetened Applesauce',   3.0, 5.0, 7.0, 'Adds mild sweetness and moisture. Directly substitutes a portion of the water/liquid in the recipe. 3–5% of total meat weight. Use cold.'],
  ],
  binder: [
    ['Non-fat Dry Milk Powder',       2.0, 4.0,  4.0, 'All-purpose binder. Pair with >=10% liquid.'],
    ['Soy Protein Concentrate',       2.0, 4.0,  4.0, 'Binds ~4x its weight in water.'],
    ['Soy Protein Isolate',           2.0, 4.0,  4.0, '90%+ protein. Binds 5x its weight in water.'],
    ['Sodium Caseinate',              2.0, 4.0,  4.0, 'Milk-derived. Outstanding fat emulsifier.'],
    ['Carrageenan (kappa)',           0.2, 0.7,  1.0, 'Very powerful. Forms firm gel on cooling.'],
    ['Dried Egg White (albumen)',     2.0, 4.0,  4.0, 'Firms texture; stable gel when cooked.'],
    ['Potato Starch',                 2.0, 4.0, 10.0, 'Water binder; improves mouthfeel.'],
    ['Corn Starch',                   2.0, 4.0, 10.0, ''],
    ['Wheat Starch',                  2.0, 4.0, 10.0, 'Contains gluten — allergen declaration required.'],
    ['Rusk (fine bread rusk)',        2.0, 4.0, 10.0, 'Classic British binder. Contains gluten.'],
    ['C-Bind (Commercial Blend)',     0.5, 0.5,  1.0, 'Typical: 0.5%.'],
    ['Whole Egg',                      3.5, 5.0,  6.0, 'Binds and adds richness. Use fresh whole eggs. 3.5–5% of total meat weight.'],
    ['Egg White (liquid)',             3.5, 5.0,  6.0, 'Lean binder — firms texture without adding fat. 3.5–5% of total meat weight.'],
    ['Tapioca Starch',                 1.5, 3.0,  5.0, 'Gluten-free binder. Dissolve in liquid BEFORE adding to mix to avoid clumping. 1.5–3% of total meat weight.'],
    ['Arrowroot Powder',               1.5, 3.0,  5.0, 'Gluten-free binder/thickener. Dissolve in liquid BEFORE adding to avoid clumping. 1.5–3% of total meat weight.'],
  ],
  cheese: [
    ['Cheddar',      5.0, 10.0, 15.0, 'Add at last grinding pass or fold in by hand.'],
    ['Pepper Jack',  5.0, 10.0, 15.0, 'Adds mild heat and pepper flavor.'],
    ['Mozzarella',   5.0, 10.0, 15.0, 'Mild and creamy.'],
    ['Habanero',     5.0, 10.0, 15.0, 'Significant heat — use sparingly.'],
    ['Swiss',        5.0, 10.0, 15.0, 'Nutty, mild flavor.'],
    ['Bleu',         5.0, 10.0, 10.0, 'Bold, pungent — a little goes a long way.'],
  ],
  phos: [
    ['Sodium Tripolyphosphate (STPP)', 0.3, 0.5, 0.5, 'HARD MAX: 0.5%. Opens protein matrix to trap water.'],
  ],
  accel: [
    ['Sodium Erythorbate',           0.5, 0.5, 0.5, 'Speeds nitrite color fixation. Add SEPARATELY from Cure #1.'],
    ['Sodium Ascorbate (Vitamin C)', 0.5, 0.5, 0.5, 'Same function as erythorbate. NEVER combine directly with Cure #1.'],
    ['Encapsulated Citric Acid',     0.5, 1.0, 1.0, 'For tangy flavor. Do NOT grind with meat.'],
    ['Acerola Cherry Powder',         0.05, 0.10, 0.15, 'Natural curing accelerator — high vitamin C. Pairs with Cultured Celery Juice Powder. Use 0.05–0.10% of total meat weight. Source: The Sausage Maker.'],
  ],
  flavor: [
    ['MSG (Monosodium Glutamate)', 0.05, 0.20, 0.5, 'Powerful umami amplifier.'],
    ['Nutritional Yeast',          0.50, 1.00, 2.0, 'Umami, cheesy flavor. Also adds protein.'],
    ['Liquid Smoke',               0.05, 0.15, 0.3, 'Use sparingly — can turn bitter at >0.3%. ~1 tsp = 4.8g. Sodium: ~170mg/100g.'],
    ['Soy Sauce (Kikkoman)',       1.00, 2.50, 4.0, 'High sodium: 5765mg/100g. Check total salt with Sodium→Salt tool. ~1 tbsp = 15.8g.'],
    ['Low-Sodium Soy Sauce',       1.00, 2.50, 4.0, 'Reduced sodium: 3200mg/100g. ~1 tbsp = 15.7g.'],
    ['Worcestershire Sauce',       0.50, 1.50, 3.0, 'Sodium: 980mg/100g. Umami depth. ~1 tbsp = 16.6g.'],
    ['Fish Sauce',                 0.50, 1.50, 2.5, 'Very high sodium: 5170mg/100g. Use sparingly. ~1 tbsp = 16.1g.'],
    ['Miso Paste (white)',         1.00, 2.00, 3.0, 'Sodium: 3520mg/100g. Umami, subtle sweetness. ~1 tbsp = 17.7g.'],
    ['Oyster Sauce',               0.50, 1.50, 2.5, 'Sodium: 2733mg/100g. Sweet-savory umami. ~1 tbsp = 17.6g.'],
    ['Dijon Mustard',              0.50, 1.00, 2.0, 'Sodium: 1240mg/100g. Emulsifier and flavor. ~1 tsp = 5.3g.'],
  ],
  spice: [
    ['Allspice (ground)',          0.05, 0.15, 0.25, ''],
    ['Annatto Powder',             0.20, 1.00, 1.00, 'Mild earthy flavor; primary use is red/orange color.'],
    ['Bay Leaf (ground)',          0.10, 0.30, 0.50, ''],
    ['Baking Soda',                0,    0.10, 0.20, 'Use with caution.'],
    ['Black Pepper (ground)',      0.15, 0.30, 0.50, ''],
    ['Cardamom (ground)',          0.025,0.03, 0.10, 'Very potent — use sparingly.'],
    ['Caraway Seed (ground)',      0.05, 0.20, 0.40, 'Defines German and Eastern European styles.'],
    ['Cayenne Pepper',             0.02, 0.10, 0.30, 'Builds heat quickly.'],
    ['Celery Seed (ground)',       0.10, 0.30, 0.50, ''],
    ['Chipotle Powder',            0.10, 0.50, 1.00, 'Smoked dried jalapeño. Adds smoky heat.'],
    ['Cinnamon (ground)',          0.05, 0.10, 0.20, ''],
    ['Cloves (ground)',            0.10, 0.20, 0.30, ''],
    ['Cumin (ground)',             0.05, 0.10, 0.20, ''],
    ['Dried Basil',                0.10, 0.50, 1.00, ''],
    ['Dried Cilantro',             0.10, 0.50, 1.00, ''],
    ['Dried Oregano',              0.10, 0.50, 1.00, 'Essential in Mediterranean and Mexican sausages.'],
    ['Dried Parsley',              0.20, 0.50, 1.00, ''],
    ['Dried Rosemary',             0.05, 0.10, 0.20, 'Potent — grind before adding.'],
    ['Dried Sumac',                0.20, 0.50, 1.00, 'Tangy, fruity-sour.'],
    ['Dried Thyme',                0.05, 0.10, 0.30, ''],
    ['Fennel Seed (ground)',       0.10, 0.30, 0.50, 'Signature Italian sausage flavor.'],
    ['Fish Sauce',                 0.20, 0.50, 1.00, 'Deep umami/salt. Reduce salt when using.'],
    ['Fresh Garlic (minced)',      0.30, 1.00, 2.00, 'Use ~4x weight of garlic powder equivalent.'],
    ['Garlic Powder',              0.10, 0.50, 1.00, ''],
    ['Ginger (ground)',            0.05, 0.15, 0.30, ''],
    ['Ground Coriander',           0.10, 0.30, 0.50, ''],
    ['Ground Mustard Seed',        0.10, 0.30, 0.50, ''],
    ['Mushroom Powder',            1.00, 3.00, 5.00, 'Powerful umami booster.'],
    ['Nutmeg (ground)',            0.05, 0.15, 0.25, 'Potent — overuse creates soapy notes.'],
    ['Onion Flakes (dried)',       0.10, 0.30, 0.50, ''],
    ['Onion Powder',               0.10, 0.30, 0.50, ''],
    ['Paprika (sweet)',            0.20, 0.50, 1.00, 'Contributes color and mild flavor.'],
    ['Paprika (smoked)',           0.10, 0.50, 1.00, ''],
    ['Paprika Extract (liquid)',   0.05, 3.00, 3.00, 'Color and mild flavor. Check supplier spec.'],
    ['Red Pepper Flakes',          0.05, 0.20, 0.50, ''],
    ['Sage (ground)',              0.10, 0.30, 0.50, 'Defines American breakfast sausage.'],
    ['Sesame Oil (toasted)',       0.20, 0.50, 1.00, 'Strong nutty flavor. Add at last mix step.'],
    ['Tomato Paste',               1.00, 3.00, 5.00, 'Adds umami, color, and slight acidity.'],
    ['Turmeric (ground)',          0.20, 0.40, 0.60, ''],
    ['White Pepper (ground)',      0.10, 0.25, 0.40, 'Milder; preferred in pale/white sausages.'],
    ['Worcestershire Sauce',       0.50, 1.00, 2.00, 'Complex umami/sweet/sour. Use cold.'],
    ['Custom Spice / Blend',       0,    2.00, 5.00, 'Type any custom spice name here.'],
  ],

  // ── Casings — stored as [name, ftPerLb, ftPerLb, ftPerLb, sourceNote, prepNote]
  // typMin = typMax = hardMax = ftPerLb reference value
  casing: [
    ['Natural Sheep 19-21mm',        7.0,  7.0,  7.0, 'Est ~7 ft/lb (small diameter)',                          'Soak overnight fridge, rinse well. Snack sticks, pepperettes.'],
    ['Natural Sheep 22-24mm',        6.0,  6.0,  6.0, 'Est ~6 ft/lb',                                           'Soak overnight fridge, rinse well. Breakfast links, small hot dogs.'],
    ['Natural Sheep 24-26mm',        5.0,  5.0,  5.0, 'Est ~5 ft/lb',                                           'Soak overnight fridge. Hot dogs, breakfast classic.'],
    ['Natural Sheep 26-28mm',        4.5,  4.5,  4.5, 'Est ~4.5 ft/lb',                                         'Soak overnight fridge. Larger breakfast or Merguez.'],
    ['Natural Hog 28-32mm',          3.0,  3.0,  3.0, 'Est ~3 ft/lb (smaller hog)',                             'Soak overnight fridge. Smaller fresh links or brat.'],
    ['Natural Hog 32-35mm',          2.0,  2.0,  2.0, 'The Sausage Maker confirmed: ~2 ft/lb',                  'Soak overnight fridge. Italian, brat, kielbasa. Grade A quality.'],
    ['Natural Hog 35-38mm',          1.8,  1.8,  1.8, 'Est ~1.8 ft/lb',                                         'Soak overnight fridge. Large kielbasa, ring sausage.'],
    ['Natural Hog 38-42mm',          1.5,  1.5,  1.5, 'lets-make-sausage.com: ~1.5 ft/lb for 38-42mm',         'Soak overnight fridge. Very large links, ring bologna.'],
    ['Collagen 17mm (snack stick)',  9.0,  9.0,  9.0, 'Est ~9 ft/lb',                                           'No soak needed. Use straight from package. Snack sticks.'],
    ['Collagen 21mm (fresh)',        6.5,  6.5,  6.5, 'Est ~6.5 ft/lb',                                         'No soak needed. Breakfast links, small links.'],
    ['Collagen 24mm (smoked)',       5.0,  5.0,  5.0, 'Est ~5 ft/lb',                                           'No soak needed. Hot dogs, smoked links.'],
    ['Collagen 32mm (smoked/fresh)', 2.0,  2.0,  2.0, 'Est ~2 ft/lb (matches natural hog 32-35mm)',            'No soak needed. Brat, Italian, kielbasa.'],
    ['Collagen 35mm (smoked/fresh)', 1.7,  1.7,  1.7, 'Est ~1.7 ft/lb',                                         'No soak needed. Larger smoked sausage.'],
    ['Fibrous 55mm (non-edible)',    0.6,  0.6,  0.6, 'Est ~0.6 ft/lb',                                         'Soak 30 min warm water. Peel before eating. Summer sausage, salami.'],
    ['Fibrous 65mm (non-edible)',    0.45, 0.45, 0.45,'Est ~0.45 ft/lb',                                        'Soak 30 min warm water. Summer sausage, bologna.'],
    ['Fibrous 76mm (non-edible)',    0.35, 0.35, 0.35,'Est ~0.35 ft/lb',                                        'Soak 30 min warm water. Bologna, deli loaf.'],
  ],
};

// MACRO_DB — keyed by ingredient name.
// Value is [protein_g, carb_g, fat_g, sodium_mg, calories_kcal] per 100g.
// (The name is the key, not a member of the array — index 0 is protein.)
//
// An ingredient with no row here is EXCLUDED from the nutrition estimate rather
// than counted as zeroes, and the panel reports how much of the mix is covered.
// Prefer adding a row over leaving a gap: a missing binder or salt used to make
// every value read low.
export const MACRO_DB = {
  'Pork Shoulder (Boston Butt)': [17.1, 0,    13.0,  62,  190],
  'Pork Loin':                   [21.0, 0,     3.5,  53,  122],
  'Beef Chuck (80/20)':          [17.2, 0,    20.0,  66,  250],
  'Beef Brisket (flat)':         [20.0, 0,     7.0,  58,  146],
  'Beef Tri-tip':                [20.5, 0,    10.0,  55,  175],
  'Sirloin Tip / Eye Round':     [22.0, 0,     4.0,  50,  128],
  'Brisket Point':               [17.5, 0,    22.0,  60,  272],
  'Chicken Thigh (boneless)':    [17.0, 0,     8.0,  75,  143],
  'Turkey Thigh (boneless)':     [19.0, 0,     5.0,  70,  121],
  'Venison':                     [23.0, 0,     2.4,  54,  120],
  'Wild Boar':                   [21.5, 0,     4.4,  53,  126],
  'Lamb Shoulder':               [17.0, 0,    16.0,  65,  213],
  'Custom Meat':                 [17.2, 0,    20.0,  66,  250],
  'Pork Back Fat':               [ 2.9, 0,    88.7,  22,  812],
  'Pork Belly (skin-off)':       [14.0, 0,    53.0,  60,  548],
  'Pork Jowl':                   [ 7.0, 0,    72.0,  40,  682],
  'Beef Fat (Suet)':             [ 1.5, 0,    94.0,  20,  854],
  'Duck Fat':                    [ 0,   0,   100.0,   0,  900],
  'Lard (rendered)':             [ 0,   0,   100.0,   0,  902],
  'Soy Protein Emulsion':        [ 9.2, 0.66, 40.4,  65,  398],  // 1:4:5 SPI/oil/water; 92% protein SPI; stated 398 kcal
  'Custom Fat':                  [ 2.9, 0,    88.7,  22,  812],
  'Cultured Celery Juice Powder':[ 12.0,65.0,  1.0, 11000, 320],  // high sodium — nitrate source
  'EcoCure #1':                  [  5.0,60.0,  2.0,   500, 280],  // polyphenol plant extract blend estimate
  'Acerola Cherry Powder':       [  0.4,95.0,  0.3,     9, 375],  // very high vitamin C
  'Whole Egg':                   [ 12.6, 0.7,  9.5,   142, 143],  // USDA large whole egg
  'Egg White (liquid)':          [ 10.9, 0.7,  0.2,   166,  52],  // USDA liquid egg white
  'Tapioca Starch':              [  0.2,88.7,  0.0,     1, 358],  // USDA
  'Arrowroot Powder':            [  0.3,88.2,  0.1,    26, 357],  // USDA
  'Unsweetened Applesauce':      [  0.2,10.7,  0.1,    27,  41],  // USDA no-sugar-added
  'Chicken Skin':                [13.3, 0,    32.4,  57,  349],
  'Cheddar':                     [25.0, 1.3,  33.1, 621,  404],
  'Pepper Jack':                 [22.0, 1.0,  30.0, 540,  357],
  'Mozzarella':                  [22.2, 2.2,  22.4, 373,  300],
  'Habanero':                    [22.0, 1.5,  28.0, 520,  350],
  'Swiss':                       [26.9, 5.4,  27.8, 192,  380],
  'Bleu':                        [21.4, 2.3,  28.7,1146,  354],
  'Ice (crushed/shaved)':        [ 0,   0,     0,    2,    0],
  'Cold Water (near 32°F)':      [ 0,   0,     0,    2,    0],
  'Beer':                        [ 0.5, 3.6,   0,    4,   43],
  'Stock (Chicken)':             [ 1.0, 0.1,   0.2, 280,   7],
  'Stock (Beef)':                [ 1.5, 0.1,   0.1, 360,   9],
  'Stock (Pork)':                [ 1.0, 0.1,   0.2, 300,   8],
  'Wine (Red)':                  [ 0.1, 2.6,   0,    4,   85],
  'Wine (White)':                [ 0.1, 2.6,   0,    4,   82],
  'Milk':                        [ 3.4, 4.8,   3.7,  44,  61],
  'Apple Juice':                 [ 0.1,11.7,   0.1,   4,  46],
  'Heavy Cream':                 [ 2.1, 2.8,  37.0,  38, 345],
  'Soy Sauce':                   [ 8.1, 4.8,   0,  5720,  53],
  'Non-fat Dry Milk Powder':     [36.2,52.0,   0.7, 535, 362],
  'Soy Protein Concentrate':     [63.6,18.0,   0.5, 250, 331],
  'Soy Protein Isolate':         [80.7, 6.6,   3.7, 650, 385],
  'Dextrose (glucose)':          [ 0,  100.0,  0,    0,  400],
  'White Granulated Sugar':      [ 0,  100.0,  0,    0,  400],
  'Brown Sugar':                 [ 0,   98.0,  0,   28,  380],
  'Nutritional Yeast':           [45.0, 38.0,  3.0,  15, 370],
  'Liquid Smoke':                [ 0.0,  1.3,  0.0,  170,   5],  // USDA
  'Soy Sauce (Kikkoman)':        [ 8.1,  6.7,  0.1, 5765,  53],  // USDA
  'Low-Sodium Soy Sauce':        [ 8.1,  6.7,  0.1, 3200,  53],  // USDA
  'Worcestershire Sauce':        [ 2.0, 18.0,  0.0,  980,  78],  // USDA
  'Fish Sauce':                  [ 5.1,  3.6,  0.0, 5170,  35],  // USDA
  'Miso Paste (white)':          [12.0, 26.5,  3.0, 3520, 199],  // USDA
  'Oyster Sauce':                [ 3.4, 23.4,  0.0, 2733, 108],  // USDA
  'Dijon Mustard':               [ 3.7, 16.1,  3.3, 1240,  66],  // USDA

  // ── Salt & curing salts ──────────────────────────────────────────────
  // All dry salts are ~39% sodium by weight; density differs between brands
  // but this table is weight-based, so the figure is the same for each.
  // Without these the sodium estimate read near zero on a cured product.
  'Non-iodized Table Salt':        [ 0, 0, 0, 38758, 0],  // USDA: salt, table
  'Kosher Salt (Diamond Crystal)': [ 0, 0, 0, 38758, 0],
  'Kosher Salt (Morton)':          [ 0, 0, 0, 38758, 0],
  'Fine Sea Salt':                 [ 0, 0, 0, 38758, 0],
  // 93.75% salt + 6.25% sodium nitrite (NaNO2 is 33.3% Na by mass)
  'Prague Powder #1 (Cure #1)':    [ 0, 0, 0, 38400, 0],

  // ── Binders ──────────────────────────────────────────────────────────
  'Potato Starch':                 [ 0.1, 83.1,  0.1,     6, 357],  // USDA
  'Corn Starch':                   [ 0.3, 91.3,  0.1,     9, 381],  // USDA
  'Wheat Starch':                  [ 0.5, 86.0,  0.5,     2, 360],
  'Rusk (fine bread rusk)':        [10.0, 75.0,  4.0,   500, 390],
  'Dried Egg White (albumen)':     [81.1,  7.8,  0.0,  1280, 382],  // USDA
  'Sodium Caseinate':              [88.0,  0.0,  1.0,  1300, 370],
  'Carrageenan (kappa)':           [ 0.2, 80.0,  0.2,   700,  83],  // mostly non-digestible
  'C-Bind (Commercial Blend)':     [10.0, 70.0,  1.0,  1000, 330],  // ESTIMATE: proprietary blend

  // ── Sweeteners ───────────────────────────────────────────────────────
  'Corn Syrup Solids':             [ 0.0, 95.0,  0.0,    50, 380],
  'Honey Powder':                  [ 0.3, 90.0,  0.0,    30, 360],  // honey + maltodextrin
  'Maple Sugar (granulated)':      [ 0.1, 90.0,  0.2,    11, 354],

  // ── Phosphates & accelerators ────────────────────────────────────────
  // Sodium figures are computed from the molecular formula.
  'Sodium Tripolyphosphate (STPP)':[ 0, 0, 0, 31250, 0],  // Na5P3O10, Na = 115/368
  'Sodium Erythorbate':            [ 0, 0, 0, 10650, 0],  // C6H7NaO6
  'Sodium Ascorbate (Vitamin C)':  [ 0, 0, 0, 11610, 0],  // C6H7NaO6
  'Encapsulated Citric Acid':      [ 0.0, 70.0, 20.0, 0, 400],  // ESTIMATE: acid in a fat coating
  'MSG (Monosodium Glutamate)':    [ 0, 0, 0, 13600, 0],  // C5H8NO4Na, Na = 23/169

  // ── Spices & seasonings (USDA FoodData Central unless noted) ─────────
  // Used at 0.5-2% of the mix, so each contributes little on its own, but 40
  // missing rows added up to a large unaccounted share.
  'Allspice (ground)':             [ 6.1, 72.1,  8.7,    77, 263],
  'Bay Leaf (ground)':             [ 7.6, 75.0,  8.4,    23, 313],
  'Baking Soda':                   [ 0, 0, 0, 27360, 0],  // NaHCO3, Na = 23/84
  'Black Pepper (ground)':         [10.4, 64.0,  3.3,    20, 251],
  'White Pepper (ground)':         [10.4, 68.6,  2.1,     5, 296],
  'Cardamom (ground)':             [10.8, 68.5,  6.7,    18, 311],
  'Caraway Seed (ground)':         [19.8, 49.9, 14.6,    17, 333],
  'Cayenne Pepper':                [12.0, 56.6, 17.3,    30, 318],
  'Red Pepper Flakes':             [12.0, 56.6, 17.3,    30, 318],
  'Celery Seed (ground)':          [18.1, 41.4, 25.3,   160, 392],
  'Chipotle Powder':               [12.0, 50.0, 14.0,    30, 282],
  'Cinnamon (ground)':             [ 4.0, 80.6,  1.2,    10, 247],
  'Cloves (ground)':               [ 6.0, 65.5, 13.0,   277, 274],
  'Cumin (ground)':                [17.8, 44.2, 22.3,   168, 375],
  'Ground Coriander':              [12.4, 55.0, 17.8,    35, 298],
  'Ground Mustard Seed':           [26.1, 28.1, 36.2,    13, 508],
  'Fennel Seed (ground)':          [15.8, 52.3, 14.9,    88, 345],
  'Ginger (ground)':               [ 9.0, 71.6,  4.2,    27, 335],
  'Nutmeg (ground)':               [ 5.8, 49.3, 36.3,    16, 525],
  'Sage (ground)':                 [10.6, 60.7, 12.8,    11, 315],
  'Turmeric (ground)':             [ 9.7, 67.1,  3.3,    27, 312],
  'Paprika (sweet)':               [14.1, 54.0, 12.9,    68, 282],
  'Paprika (smoked)':              [14.1, 54.0, 12.9,    68, 282],
  'Paprika Extract (liquid)':      [ 0.0,  5.0, 90.0,     0, 850],  // ESTIMATE: oleoresin
  'Annatto Powder':                [ 3.0, 65.0,  5.0,    50, 300],  // ESTIMATE: limited data
  'Dried Basil':                   [23.0, 47.8,  4.1,    76, 233],
  'Dried Cilantro':                [21.9, 52.1,  4.8,   211, 279],
  'Dried Oregano':                 [ 9.0, 68.9,  4.3,    25, 265],
  'Dried Parsley':                 [26.6, 50.6,  5.5,   452, 292],
  'Dried Rosemary':                [ 4.9, 64.1, 15.2,    50, 331],
  'Dried Thyme':                   [ 9.1, 63.9,  7.4,    55, 276],
  'Dried Sumac':                   [ 3.0, 75.0,  7.0,    30, 320],  // ESTIMATE: limited data
  'Garlic Powder':                 [16.6, 72.7,  0.7,    60, 331],
  'Fresh Garlic (minced)':         [ 6.4, 33.1,  0.5,    17, 149],
  'Onion Powder':                  [10.4, 79.1,  1.0,    73, 341],
  'Onion Flakes (dried)':          [ 9.0, 83.3,  0.5,    73, 349],
  'Mushroom Powder':               [25.0, 55.0,  2.0,    20, 300],  // ESTIMATE: dried mushroom
  'Tomato Paste':                  [ 4.3, 18.9,  0.5,    59,  82],
  'Sesame Oil (toasted)':          [ 0.0,  0.0,100.0,     0, 884],
  'Custom Spice / Blend':          [10.0, 60.0,  8.0,    50, 280],  // ESTIMATE: generic ground spice
};

// CASING_DB [label, avgDiamMM, ftPerLb, prepNote, source]
// ftPerLb = feet of casing needed per pound of stuffable meat mix
// This is the PRIMARY calculation field — based on real-world data not theoretical fill rates
// Users can override ftPerLb in the Ingredient Database for their specific stuffing style
export const CASING_DB = [
  ['Natural Sheep 19-21mm',        20,  7.0, 'Soak overnight in fridge, rinse well.',                  'Est ~7 ft/lb (small diameter)'],
  ['Natural Sheep 22-24mm',        23,  6.0, 'Soak overnight in fridge, rinse well.',                  'Est ~6 ft/lb'],
  ['Natural Sheep 24-26mm',        25,  5.0, 'Soak overnight in fridge. Hot dog / breakfast classic.', 'Est ~5 ft/lb'],
  ['Natural Sheep 26-28mm',        27,  4.5, 'Soak overnight in fridge. Larger breakfast or Merguez.', 'Est ~4.5 ft/lb'],
  ['Natural Hog 28-32mm',          30,  3.0, 'Soak overnight in fridge. Smaller fresh links or brat.', 'Est ~3 ft/lb'],
  ['Natural Hog 32-35mm',        33.5,  2.0, 'Soak overnight in fridge. Italian, brat, kielbasa.',     'The Sausage Maker: ~2 ft/lb confirmed'],
  ['Natural Hog 35-38mm',        36.5,  1.8, 'Soak overnight in fridge. Large kielbasa, ring sausage.','Est ~1.8 ft/lb'],
  ['Natural Hog 38-42mm',          40,  1.5, 'Soak overnight in fridge. Very large links, ring bologna.','~1.5 ft/lb (lets-make-sausage.com)'],
  ['Collagen 17mm (snack stick)',  17,  9.0, 'No soak needed. Use straight from package.',             'Est ~9 ft/lb'],
  ['Collagen 21mm (fresh)',        21,  6.5, 'No soak needed. Breakfast links, small links.',          'Est ~6.5 ft/lb'],
  ['Collagen 24mm (smoked)',       24,  5.0, 'No soak needed. Hot dogs, smoked links.',                'Est ~5 ft/lb'],
  ['Collagen 32mm (smoked/fresh)', 32,  2.0, 'No soak needed. Brat, Italian, kielbasa.',              'Est ~2 ft/lb'],
  ['Collagen 35mm (smoked/fresh)', 35,  1.7, 'No soak needed. Larger smoked sausage.',                'Est ~1.7 ft/lb'],
  ['Fibrous 55mm (non-edible)',    55,  0.6, 'Soak 30 min warm water. Peel before eating.',           'Est ~0.6 ft/lb'],
  ['Fibrous 65mm (non-edible)',    65, 0.45, 'Soak 30 min warm water. Summer sausage, bologna.',      'Est ~0.45 ft/lb'],
  ['Fibrous 76mm (non-edible)',    76, 0.35, 'Soak 30 min warm water. Bologna, deli loaf.',           'Est ~0.35 ft/lb'],
];

// STYLE_CASING — sausage style to recommended casing
export const STYLE_CASING = [
  ['Snack Stick',                  'Sheep 19-21mm or Collagen 17-21mm'],
  ['Breakfast Link / Chipolata',   'Sheep 22-26mm or Collagen 21-24mm'],
  ['Hot Dog / Frank / Wiener',     'Sheep 24-26mm (traditional)'],
  ['Merguez',                      'Sheep 24-26mm'],
  ['Chorizo (fresh or smoked)',    'Sheep 24-26mm or Hog 32-35mm'],
  ['Italian Sausage (sweet/hot)',  'Hog 32-35mm (traditional)'],
  ['Bratwurst / Bockwurst',        'Hog 30-35mm (traditional)'],
  ['Knockwurst',                   'Hog 32-35mm'],
  ['Kielbasa / Polish Sausage',    'Hog 32-38mm (traditional)'],
  ['Andouille',                    'Hog 32-38mm'],
  ['Smoked Sausage (generic)',     'Hog 32-38mm'],
  ['Beer Sausage / Mettwurst',     'Hog 32-35mm'],
  ['Summer Sausage',               'Fibrous 55-65mm (non-edible, peel before eating)'],
  ['Bologna / Mortadella',         'Fibrous 65-76mm (non-edible, peel before eating)'],
  ['Custom / Other',               'Choose casing manually below'],
];

export const COOK_METHODS = ['Hot Smoked', 'Cold Smoked', 'Cooked (Oven/Stovetop)'];
export const UNIT_SYSTEMS  = ['Metric (g)', 'Imperial (lbs)'];

// ── UNIT OPTIONS for cost entry ──────────────────────────────
export const UNIT_OPTIONS = [
  { value: 'lb',  label: '$ / lb',  toG: 453.592  },
  { value: 'oz',  label: '$ / oz',  toG: 28.3495  },
  { value: 'g',   label: '$ / g',   toG: 1        },
  { value: 'kg',  label: '$ / kg',  toG: 1000     },
  { value: 'yd',  label: '$ / yd',  toG: null     },  // casings: cost per yard
  { value: 'ft',  label: '$ / ft',  toG: null     },  // casings: cost per foot
];

export const UNIT_TO_FT = { yd: 3, ft: 1 };  // for casing cost calculation

export const UNIT_TO_G = {
  lb: 453.592,
  oz: 28.3495,
  g:  1,
  kg: 1000,
};

// Default cost unit per section
export const DEFAULT_COST_UNIT = {
  meat:   'lb',
  fat:    'lb',
  liquid: 'g',
  salt:   'oz',
  cure:   'oz',
  sweet:  'oz',
  binder: 'oz',
  cheese: 'oz',
  phos:   'oz',
  accel:  'oz',
  flavor: 'oz',
  spice:  'oz',
  casing: 'yd',
};

// ── Build full ingredient objects from DB + MACRO_DB ──────────
// Used to initialise localStorage on first load
export function buildDefaultIngredientDB() {
  const result = {};
  SECTIONS.forEach(sect => {
    const entries = DB[sect.key] || [];
    result[sect.key] = entries.map(entry => {
      // Casing entries have extra fields: [name, ftPerLb, ftPerLb, ftPerLb, note, prepNote]
      if (sect.isCasingSection) {
        return {
          id:          entry[0].toLowerCase().replace(/[^a-z0-9()/-]/g,'_'),
          name:        entry[0],
          typMin:      entry[1],  // ftPerLb reference value
          typMax:      entry[2],
          hardMax:     entry[3],
          note:        entry[4] || '',
          prepNote:    entry[5] || '',
          macro:       null,
          costPerUnit: 0,
          costUnit:    'yd',      // casings sold by the yard/foot
          isCustom:    false,
          isCasing:    true,
        };
      }
      return {
        id:          entry[0].toLowerCase().replace(/[^a-z0-9]/g,'_'),
        name:        entry[0],
        typMin:      entry[1],
        typMax:      entry[2],
        hardMax:     entry[3],
        note:        entry[4] || '',
        macro:       MACRO_DB[entry[0]] || null,
        costPerUnit: 0,
        costUnit:    DEFAULT_COST_UNIT[sect.key] || 'oz',
        isCustom:    false,
      };
    });
  });
  return result;
}
